import React, { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import moment from 'moment'

interface ItemsPerHourChartProps {
  timelineData: Array<{
    separated_hour: string
    total_items: number
    avg_secs: number
  }>
  title?: string
  onBarClick?: (hourData: any) => void
}

const ItemsPerHourChart: React.FC<ItemsPerHourChartProps> = ({ 
  timelineData, 
  title = 'Itens Processados por Hora',
  onBarClick 
}) => {
  // Processar e ordenar dados por horário
  const processedData = useMemo(() => {
    return timelineData.map(entry => ({
      hour: moment(entry.separated_hour).format('HH:mm'),
      total_items: entry.total_items,
      // Adicionar ordem customizada para turno noturno (madrugada no final)
      sortOrder: moment(entry.separated_hour).hour() <= 4 ?
        moment(entry.separated_hour).hour() + 24 : // 00h-04h vão para 24h-28h
        moment(entry.separated_hour).hour()
    })).sort((a, b) => a.sortOrder - b.sortOrder)
  }, [timelineData])

  const categories = processedData.map(item => item.hour)

  const seriesData = processedData.map(item => item.total_items)

  // Calcular média de itens por hora
  const averageItems = seriesData.length > 0
    ? seriesData.reduce((acc, val) => acc + val, 0) / seriesData.length
    : 0

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: {
        show: false,
      },
      fontFamily: 'Poppins, sans-serif',
      events: {
        dataPointSelection: (event, chartContext, config) => {
          if (onBarClick) {
            const selectedData = timelineData[config.dataPointIndex]
            onBarClick(selectedData)
          }
        },
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: '60%',
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => val.toLocaleString('pt-BR'),
      offsetY: -25,
      style: {
        fontSize: '11px',
        colors: ['#304758'],
        fontWeight: 600,
      },
    },
    xaxis: {
      categories,
      labels: {
        rotate: processedData.length > 8 ? -45 : processedData.length > 5 ? -30 : 0,
        style: { fontSize: '11px' },
        maxHeight: processedData.length > 8 ? 80 : 60,
        trim: false,
        hideOverlappingLabels: false,
      },
    },
    yaxis: {
      title: {
        text: 'Quantidade de Itens',
        style: {
          fontSize: '12px',
          fontWeight: 500,
        },
      },
      labels: {
        formatter: (val: number) => Math.floor(val).toLocaleString('pt-BR'),
      },
    },
    colors: ['#FF6B35'],
    fill: {
      type: 'solid',
      opacity: 0.9,
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val.toLocaleString('pt-BR')} itens`,
      },
    },
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4,
    },
    annotations: {
      yaxis: averageItems > 0
        ? [
            {
              y: averageItems,
              borderColor: '#f5576c',
              strokeDashArray: 5,
              borderWidth: 2,
              label: {
                text: `Média: ${averageItems.toFixed(1)} itens/h`,
                style: {
                  background: '#f5576c',
                  color: '#fff',
                  fontSize: '11px',
                  fontWeight: 600,
                },
                position: 'right',
              },
            },
          ]
        : [],
    },
  }

  const series = [
    {
      name: 'Itens Processados',
      data: seriesData,
    },
  ]

  return (
    <div>
      {title && (
        <h3 style={{ marginBottom: '1rem', fontSize: '16px', fontWeight: 500 }}>
          {title}
        </h3>
      )}
      <ReactApexChart options={options} series={series} type="bar" height={350} />
    </div>
  )
}

export default ItemsPerHourChart

